"use client";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/shadcn/alert-dialog";
import { Button } from "@/components/shadcn/button";
import { useSession } from "@/lib/auth/client";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { leaveWebinarEvent } from "../../_actions/webinar/leave-webinar-event";
import { useEventForm } from "../event-form-context";
import { WebinarEventWithUserStatus } from "./types";

interface WebinarJoinButtonProps {
    event: WebinarEventWithUserStatus;
}

export function WebinarJoinButton({ event }: WebinarJoinButtonProps) {
    const t = useTranslations();
    const router = useRouter();
    const { data: session } = useSession();
    const {
        openWebinarModal,
        openLoginRequiredModal,
        openPremiumRequiredModal,
    } = useEventForm();
    const [showLeaveAlert, setShowLeaveAlert] = useState(false);

    const isAuthenticated = session?.user !== undefined;
    const hasPremium = session?.user?.isPremium ?? false;

    const participantCount = event.participantCount;
    const isFull = participantCount >= event.capacity;
    const isJoined = event.isUserJoined;
    const isActive = event.status === "active";
    const isEnded = event.status === "ended";

    const leaveMutation = useMutation({
        mutationFn: () => leaveWebinarEvent(event.id),
        onSuccess: (result) => {
            if (!result.success) {
                toast.error(result.error.message || t("events.errorGeneric"));
            } else {
                toast.success(result.message || t("events.leftWebinar"));
                router.refresh();
            }
        },
        onError: () => {
            toast.error(t("events.errorGeneric"));
        },
    });

    const handleJoin = () => {
        // First check: user must be authenticated
        if (!isAuthenticated) {
            openLoginRequiredModal();
            return;
        }

        // Second check: if event is premium, user must have premium membership
        if (event.isPremium && !hasPremium) {
            openPremiumRequiredModal();
            return;
        }

        // If both checks pass, show the registration form modal
        openWebinarModal({
            eventId: event.id,
            eventTitle: event.title,
        });
    };

    const handleLeave = () => {
        setShowLeaveAlert(false);
        leaveMutation.mutate();
    };

    // If event has ended, show appropriate message
    if (isEnded) {
        return (
            <Button variant="secondary" disabled className="w-full">
                {t("events.webinarEnded")}
            </Button>
        );
    }

    // If event is currently active, check if we're still in the first half
    if (isActive) {
        // Calculate cutoff time (first half of webinar)
        const cutoffTime = new Date(
            event.date.getTime() + (event.duration / 2) * 60000,
        );
        const now = new Date();

        // If past the first half, show disabled button
        if (now > cutoffTime) {
            return (
                <Button variant="secondary" disabled className="w-full">
                    {t("events.webinarOngoing")}
                </Button>
            );
        }
        // Otherwise, allow joining (fall through to normal join logic)
    }

    if (isFull && !isJoined) {
        return (
            <Button variant="outline" disabled className="w-full">
                Kontenjan Dolu
            </Button>
        );
    }

    if (isJoined) {
        return (
            <>
                <Button
                    variant="outline"
                    onClick={() => setShowLeaveAlert(true)}
                    disabled={leaveMutation.isPending}
                    className="w-full"
                >
                    {leaveMutation.isPending
                        ? t("events.processing")
                        : t("events.leaveParticipation")}
                </Button>
                <AlertDialog
                    open={showLeaveAlert}
                    onOpenChange={setShowLeaveAlert}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {t("events.leaveWebinarConfirmTitle")}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {t("events.leaveWebinarConfirmBody")}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>
                                {t("common.cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction onClick={handleLeave}>
                                {t("events.leave")}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </>
        );
    }

    return (
        <Button
            onClick={handleJoin}
            disabled={leaveMutation.isPending}
            className="w-full"
        >
            {leaveMutation.isPending
                ? t("events.processing")
                : t("events.submit")}
        </Button>
    );
}
